import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { LocalDate } from "@/components/ui/local-date";
import { ApiError, api } from "@/lib/api";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useState } from "react";

/**
 * `/accounting/tax-documents` — the tax portal kept in Hub: returns the
 * company filed and what the office sent, with their files, and the
 * movements on the tax account.
 */

type TaxDocument = {
  id: string;
  reference: string;
  kind: string;
  direction: "filed" | "received";
  taxType: string | null;
  deliveredOn: string;
  periodFrom: string | null;
  periodTo: string | null;
  files: Array<{ id: string; name: string; byteSize: number }>;
};

type Movement = {
  id: string;
  bookedOn: string;
  taxType: string;
  description: string;
  debit: string | null;
  credit: string | null;
};

export const Route = createFileRoute("/_app/accounting/tax-documents")({
  loader: async () => {
    try {
      const [docs, account] = await Promise.all([
        api.get<{ documents: TaxDocument[] }>("/api/accounting/tax-documents"),
        api.get<{ movements: Movement[] }>("/api/accounting/tax-account"),
      ]);
      return { documents: docs.documents, movements: account.movements };
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) throw redirect({ to: "/my" });
      throw err;
    }
  },
  component: TaxDocumentsPage,
});

function money(value: string | null): string {
  if (value === null) return "";
  return Number(value).toLocaleString("cs-CZ", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function TaxDocumentsPage() {
  const { documents, movements } = Route.useLoaderData();
  const years = [...new Set(documents.map((d) => d.deliveredOn.slice(0, 4)))];
  const [year, setYear] = useState<string>(years[0] ?? "");
  const shown = documents.filter((d) => d.deliveredOn.startsWith(year));

  return (
    <div className="space-y-6 p-6">
      <div className="space-y-1">
        <h1 className="font-semibold text-2xl">Tax documents</h1>
        <p className="text-muted-foreground text-sm">
          What the company filed with the tax office and what the office sent, from the MOJE daně
          portal, with every file it offered.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Documents</CardTitle>
          <CardDescription className="flex flex-wrap gap-2 pt-1">
            {years.map((y) => (
              <button
                key={y}
                type="button"
                onClick={() => setYear(y)}
                className={y === year ? "font-semibold text-foreground" : "hover:underline"}
              >
                {y}
              </button>
            ))}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-1 text-sm">
          {shown.length === 0 ? (
            <p className="text-muted-foreground">No documents imported yet.</p>
          ) : (
            shown.map((d) => (
              <div
                key={d.id}
                className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b py-1.5"
              >
                <span className="w-24 shrink-0 tabular-nums">
                  <LocalDate value={d.deliveredOn} />
                </span>
                <Badge variant={d.direction === "filed" ? "secondary" : "outline"}>
                  {d.direction === "filed" ? "filed" : "from the office"}
                </Badge>
                <span className="min-w-64 flex-1 font-medium">{d.kind}</span>
                {d.periodFrom && d.periodTo && (
                  <span className="text-muted-foreground">
                    <LocalDate value={d.periodFrom} /> – <LocalDate value={d.periodTo} />
                  </span>
                )}
                <span className="font-mono text-muted-foreground text-xs">{d.reference}</span>
                <span className="flex w-full flex-wrap gap-x-3 pl-24 text-xs">
                  {d.files.length === 0 ? (
                    <span className="text-muted-foreground">no files</span>
                  ) : (
                    d.files.map((f) => (
                      <a
                        key={f.id}
                        href={`/api/accounting/tax-documents/files/${f.id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="underline"
                      >
                        {f.name}
                      </a>
                    ))
                  )}
                </span>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Tax account</CardTitle>
          <CardDescription>
            Movements on the company's tax account at the office (osobní daňový účet).
          </CardDescription>
        </CardHeader>
        <CardContent className="text-sm">
          {movements.length === 0 ? (
            <p className="text-muted-foreground">No movements imported yet.</p>
          ) : (
            <table className="w-full">
              <thead className="text-left text-muted-foreground text-xs">
                <tr>
                  <th className="py-1 font-normal">Date</th>
                  <th className="font-normal">Tax</th>
                  <th className="font-normal">Movement</th>
                  <th className="text-right font-normal">Owed</th>
                  <th className="text-right font-normal">Paid</th>
                </tr>
              </thead>
              <tbody>
                {movements.map((m) => (
                  <tr key={m.id} className="border-t">
                    <td className="py-1 tabular-nums">
                      <LocalDate value={m.bookedOn} />
                    </td>
                    <td>{m.taxType}</td>
                    <td>{m.description}</td>
                    <td className="text-right tabular-nums">{money(m.debit)}</td>
                    <td className="text-right tabular-nums">{money(m.credit)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
